import type { DrokMyCopy, DrokToolGroupName } from "@rakazo/contracts";
import { drokToolkitKey } from "@rakazo/contracts";
import { type DrokCopiesState, useDrokCopies } from "./use-drok-copies";

/** Pure form of the checks below, so every surface decides the same way. */
export function copyOf(
  copies: readonly DrokMyCopy[] | undefined,
  botId: string | undefined,
): DrokMyCopy | undefined {
  return botId ? copies?.find((copy) => copy.botId === botId) : undefined;
}

export function copyHasGroup(copy: DrokMyCopy | undefined, group: DrokToolGroupName): boolean {
  return copy?.toolGroups.includes(group) === true;
}

export function copyAllowsToolkit(copy: DrokMyCopy | undefined, slug: string): boolean {
  const key = drokToolkitKey(slug);
  return copy?.toolkits.some((toolkit) => drokToolkitKey(toolkit.slug) === key) === true;
}

/** The server accepts computer calls for this bot only on a copy with the computer group. */
export function canUseComputerOf(state: DrokCopiesState, botId: string | undefined): boolean {
  if (state.unrestricted) return true;
  return copyHasGroup(copyOf(state.copies, botId), "computer");
}

/** The terminal and files need the shell group. */
export function canUseShellOf(state: DrokCopiesState, botId: string | undefined): boolean {
  if (state.unrestricted) return true;
  return copyHasGroup(copyOf(state.copies, botId), "shell");
}

/** connections.begin takes a Composio slug allowed on any of the caller's copies; the card names its bot. */
export function canConnectToolkitOf(
  state: DrokCopiesState,
  botId: string | undefined,
  connectorId: string | undefined,
  slug: string,
): boolean {
  if (state.unrestricted) return true;
  return (
    (connectorId === "composio" || connectorId === undefined) &&
    copyAllowsToolkit(copyOf(state.copies, botId), slug)
  );
}

export function useCanUseComputer(botId: string | undefined): boolean {
  return canUseComputerOf(useDrokCopies(), botId);
}

export function useCanUseShell(botId: string | undefined): boolean {
  return canUseShellOf(useDrokCopies(), botId);
}

export function useCanConnectToolkit(
  botId: string | undefined,
  connectorId: string | undefined,
  slug: string,
): boolean {
  return canConnectToolkitOf(useDrokCopies(), botId, connectorId, slug);
}
